import { Card, Col, Form, Input, InputNumber, Row, Select, Radio, Button, Collapse, Drawer } from "antd";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { PlusOutlined, CloseOutlined, TableOutlined, EditOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Panel } = Collapse;

const AddTableForm = forwardRef((props, ref) => {
  const { view, onDelete, index, initialData, activeKeys, onAccordionChange, tableForms, labelForms } = props;

  const [panelTitle, setPanelTitle] = useState(`Table ${index + 1}`);
  const [isFormReady, setIsFormReady] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [isSplitColumn, setIsSplitColumn] = useState(false);
  const [splitColumns, setSplitColumns] = useState([{ id: 1, column_name: "", set_value: "", calculation_rule: "" }]);
  const [hasTableHeader, setHasTableHeader] = useState(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [showSplitColumnValidation, setShowSplitColumnValidation] = useState(false);

  const [modalForm] = Form.useForm();
  const [form] = Form.useForm();

  const variables = useSelector((state) => state.packingReportTemplate.variables);

  useEffect(() => {
    setIsFormReady(true);
  }, []);

  useEffect(() => {
    if (initialData && form && isFormReady) {
      form.setFieldsValue({
        table_name: initialData.table_name,
        from_row: initialData.from_row ? parseInt(initialData.from_row) : null,
        has_table_header: initialData.has_table_header !== undefined ? initialData.has_table_header : 0,
        table_header: initialData.table_header || "",
      });

      if (initialData.table_name) {
        setPanelTitle(initialData.table_name);
      }

      if (initialData.has_table_header) {
        setHasTableHeader(true);
      }

      if (initialData.columns && Array.isArray(initialData.columns)) {
        setColumns(initialData.columns);
      }
    }
  }, [initialData, form, isFormReady]);

  useImperativeHandle(ref, () => ({
    async submit() {
      const values = await form.validateFields();
      return {
        ...values,
        columns: columns,
      };
    },
    discart() {
      form.resetFields();
      setColumns([]);
    },
    validateFields() {
      return form.validateFields().then((values) => ({
        ...values,
        columns: columns,
      }));
    },
  }));

  const showModal = (columnIndex = null) => {
    setShowSplitColumnValidation(false);
    if (columnIndex !== null) {
      const columnToEdit = columns[columnIndex];
      setEditingColumnIndex(columnIndex);
      modalForm.setFieldsValue({
        column_name: columnToEdit.column_name,
        set_value: columnToEdit.set_value,
        merge_from_column: columnToEdit.merge_from_column,
        merge_to_column: columnToEdit.merge_to_column,
        calculation_rule: columnToEdit.calculation_rule,
        is_split: columnToEdit.is_split ? 1 : 0,
      });

      if (columnToEdit.is_split && columnToEdit.split_columns) {
        setIsSplitColumn(true);
        setSplitColumns(columnToEdit.split_columns);
      }
    } else {
      setEditingColumnIndex(null);
      modalForm.resetFields();
      setIsSplitColumn(false);
      setSplitColumns([{ id: Date.now(), column_name: "", set_value: "", calculation_rule: "" }]);
    }
    setIsDrawerOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await modalForm.validateFields();

      if (isSplitColumn) {
        const hasEmptyColumnName = splitColumns.some((col) => !col.column_name || col.column_name.trim() === "");
        if (hasEmptyColumnName) {
          setShowSplitColumnValidation(true);

          return;
        }
      }

      const columnData = {
        column_name: values.column_name,
        set_value: values.set_value,
        merge_from_column: values.merge_from_column || null,
        merge_to_column: values.merge_to_column || null,
        calculation_rule: values.calculation_rule || null,
        is_split: isSplitColumn,
        split_columns: isSplitColumn ? splitColumns : null,
      };

      if (editingColumnIndex !== null) {
        const updatedColumns = [...columns];
        updatedColumns[editingColumnIndex] = columnData;
        setColumns(updatedColumns);
      } else {
        setColumns([...columns, columnData]);
      }

      setIsDrawerOpen(false);
      modalForm.resetFields();
      setIsSplitColumn(false);
      setSplitColumns([{ id: Date.now(), column_name: "", set_value: "", calculation_rule: "" }]);
      setEditingColumnIndex(null);
      setShowSplitColumnValidation(false);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleModalCancel = () => {
    setIsDrawerOpen(false);
    modalForm.resetFields();
    setIsSplitColumn(false);
    setSplitColumns([{ id: Date.now(), column_name: "", set_value: "", calculation_rule: "" }]);
    setEditingColumnIndex(null);
    setShowSplitColumnValidation(false);
  };

  const addSplitColumn = () => {
    setSplitColumns([...splitColumns, { id: Date.now(), column_name: "", set_value: "", calculation_rule: "" }]);
  };

  const removeSplitColumn = (id) => {
    if (splitColumns.length > 1) {
      setSplitColumns(splitColumns.filter((col) => col.id !== id));
    }
  };

  const updateSplitColumn = (id, field, value) => {
    setSplitColumns(splitColumns.map((col) => (col.id === id ? { ...col, [field]: value } : col)));
  };

  const removeColumn = (columnIndex) => {
    setColumns(columns.filter((_, i) => i !== columnIndex));
  };

  const getTableRowSpan = useCallback((hasHeaderValue, hasSubHeaderValue) => {
    let span = 1;
    if (hasHeaderValue) {
      span += 1;
    }
    if (hasSubHeaderValue) {
      span += 1;
    }
    return span;
  }, []);

  const extractExistingRanges = useCallback(() => {
    const ranges = [];

    if (tableForms && Array.isArray(tableForms)) {
      tableForms.forEach((tableForm, tableIdx) => {
        if (!tableForm) return;
        if (tableIdx === index) return;

        const tableData = tableForm.initialData || {};
        if (tableData.from_row == null) return;

        const otherFromRow = Number(tableData.from_row);
        if (Number.isNaN(otherFromRow)) return;

        const otherHasHeader = tableData.has_table_header === 1 || tableData.has_table_header === true;
        const otherHasSubHeader =
          Array.isArray(tableData.columns) &&
          tableData.columns.some(
            (col) => col?.is_split && Array.isArray(col.split_columns) && col.split_columns.length > 0,
          );

        const span = getTableRowSpan(otherHasHeader, otherHasSubHeader);
        const otherToRow = otherFromRow + span - 1;

        ranges.push({
          type: "table",
          fromRow: otherFromRow,
          toRow: otherToRow,
          hasHeader: otherHasHeader,
          hasSubHeader: otherHasSubHeader,
        });
      });
    }

    if (labelForms && Array.isArray(labelForms)) {
      labelForms.forEach((labelForm) => {
        if (!labelForm || !labelForm.initialData) return;
        const labelFromRow = labelForm.initialData.from_row != null ? Number(labelForm.initialData.from_row) : null;
        if (labelFromRow == null || Number.isNaN(labelFromRow)) return;
        const labelToRow =
          labelForm.initialData.to_row != null && !Number.isNaN(Number(labelForm.initialData.to_row))
            ? Number(labelForm.initialData.to_row)
            : labelFromRow;

        ranges.push({
          type: "label",
          fromRow: labelFromRow,
          toRow: labelToRow,
        });
      });
    }

    return ranges.sort((a, b) => {
      if (a.toRow === b.toRow) {
        return a.fromRow - b.fromRow;
      }
      return a.toRow - b.toRow;
    });
  }, [getTableRowSpan, tableForms, labelForms, index]);

  const resolveTablePlacement = useCallback(() => {
    if (!form || !isFormReady) return;

    const formValues = form.getFieldsValue(["from_row", "has_table_header"]);
    let proposedFromRow =
      formValues?.from_row != null && !Number.isNaN(Number(formValues.from_row)) ? Number(formValues.from_row) : null;

    const currentHasHeader = formValues?.has_table_header === 1 || formValues?.has_table_header === true;
    const currentHasSubHeader = columns.some(
      (col) => col?.is_split && Array.isArray(col.split_columns) && col.split_columns.length > 0,
    );

    const existingRanges = extractExistingRanges();
    const span = getTableRowSpan(currentHasHeader, currentHasSubHeader);

    const determineNextFromRow = () => {
      if (existingRanges.length === 0) {
        return 1;
      }

      const lastRange = existingRanges[existingRanges.length - 1];

      if (lastRange.type === "label") {
        return lastRange.toRow + 2;
      }

      return lastRange.toRow + 1;
    };

    if (proposedFromRow == null) {
      proposedFromRow = determineNextFromRow();
    }

    if (proposedFromRow == null) return;

    const adjustForOverlap = () => {
      let candidateFrom = proposedFromRow;
      let candidateTo = candidateFrom + span - 1;
      const sortedRanges = existingRanges.slice().sort((a, b) => a.fromRow - b.fromRow);

      const overlaps = (range) => !(candidateTo < range.fromRow || candidateFrom > range.toRow);

      const calculateShift = (range) => {
        if (range.type === "label") {
          return range.toRow + 2;
        }

        return range.toRow + 1;
      };

      let conflict = sortedRanges.find(overlaps);

      while (conflict) {
        candidateFrom = calculateShift(conflict);
        candidateTo = candidateFrom + span - 1;
        conflict = sortedRanges.find(overlaps);
      }

      return { from: candidateFrom, to: candidateTo };
    };

    const resolvedRange = adjustForOverlap();

    if (resolvedRange && resolvedRange.from !== proposedFromRow) {
      form.setFieldsValue({ from_row: resolvedRange.from });
    } else if (!resolvedRange && proposedFromRow !== formValues?.from_row) {
      form.setFieldsValue({ from_row: proposedFromRow });
    }
  }, [columns, extractExistingRanges, form, getTableRowSpan, isFormReady]);

  useEffect(() => {
    resolveTablePlacement();
  }, [resolveTablePlacement]);

  const validateRowOverlap = (_, value) => {
    if (!form || value == null) return Promise.resolve();

    const currentHasHeader = form.getFieldValue("has_table_header");
    const currentTableRows = [value];

    if (currentHasHeader === 1) {
      currentTableRows.push(value + 1);
    }

    if (tableForms && Array.isArray(tableForms)) {
      for (let i = 0; i < tableForms.length; i++) {
        if (i === index) continue;

        const otherTableData = tableForms[i];
        let otherFromRow, otherHasHeader;

        if (otherTableData?.initialData?.from_row) {
          otherFromRow = Number(otherTableData.initialData.from_row);
          otherHasHeader = otherTableData.initialData.has_table_header;
        }

        if (otherFromRow != null) {
          const otherTableRows = [otherFromRow];

          if (otherHasHeader === 1) {
            otherTableRows.push(otherFromRow + 1);
          }

          for (const currentRow of currentTableRows) {
            if (otherTableRows.includes(currentRow)) {
              return Promise.reject(
                new Error(
                  `Row ${currentRow} overlaps with another table${otherHasHeader === 1 ? " (with header)" : ""}`,
                ),
              );
            }
          }
        }
      }
    }

    if (labelForms && Array.isArray(labelForms)) {
      for (let i = 0; i < labelForms.length; i++) {
        const labelData = labelForms[i];
        let labelFromRow, labelToRow;

        if (labelData?.initialData) {
          labelFromRow = Number(labelData.initialData.from_row);
          labelToRow = Number(labelData.initialData.to_row);
        }

        for (const currentRow of currentTableRows) {
          if (labelFromRow != null && labelToRow != null) {
            if (currentRow >= labelFromRow && currentRow <= labelToRow) {
              return Promise.reject(
                new Error(`Row ${currentRow} overlaps with a label (rows ${labelFromRow}-${labelToRow})`),
              );
            }
          } else if (labelFromRow != null && currentRow === labelFromRow) {
            return Promise.reject(new Error(`Row ${currentRow} overlaps with a label`));
          }
        }
      }
    }

    return Promise.resolve();
  };

  return (
    <>
      <Collapse activeKey={activeKeys} onChange={onAccordionChange} className="mb-2">
        <Panel
          header={
            <span>
              <TableOutlined style={{ marginRight: 8, color: "#52c41a" }} />
              {panelTitle}
            </span>
          }
          key={`table-${index}`}
          extra={
            !view && (
              <Button
                danger
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
              >
                Delete
              </Button>
            )
          }
        >
          <div className="containt-body">
            <Form
              layout="vertical"
              form={form}
              name={`table-form-${index}`}
              onValuesChange={(changedValues) => {
                if (changedValues.table_name) {
                  setPanelTitle(changedValues.table_name || `Table ${index + 1}`);
                }
                if (changedValues.has_table_header !== undefined) {
                  setHasTableHeader(changedValues.has_table_header === 1);

                  form.validateFields(["from_row"]).catch(() => {});
                }
              }}
            >
              <Card>
                <Row gutter={[12, 8]}>
                  <Col className="gutter-row" span={5}>
                    <Form.Item
                      label="Table Name"
                      name="table_name"
                      rules={[
                        {
                          required: true,
                          message: "Table Name is required",
                        },
                      ]}
                    >
                      <Input disabled={view} size="small" placeholder="Name" />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" span={5}>
                    <Form.Item
                      label="From Row"
                      name="from_row"
                      rules={[
                        {
                          validator: validateRowOverlap,
                        },
                      ]}
                    >
                      <InputNumber
                        className="w-100"
                        disabled={view}
                        size="small"
                        placeholder="From Row"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" span={4}>
                    <Form.Item label="Has Table Header" name="has_table_header" initialValue={0}>
                      <Radio.Group disabled={view} size="small">
                        <Radio value={1}>Yes</Radio>
                        <Radio value={0}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  {/* {hasTableHeader && ( */}
                  <Col className="gutter-row" span={5}>
                    <Form.Item
                      label="Table Header"
                      name="table_header"
                      rules={[
                        {
                          required: hasTableHeader,
                          message: "Table Header is required",
                        },
                      ]}
                    >
                      <Input disabled={view} size="small" placeholder="Table Header" />
                    </Form.Item>
                  </Col>
                  {/* )} */}
                  <Col className="gutter-row" span={5}>
                    <Form.Item label={hasTableHeader ? " " : " "} colon={false}>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => showModal()}
                        disabled={view}
                        block={!hasTableHeader}
                        style={{ width: "100%" }}
                      >
                        <PlusOutlined /> Add Column
                      </Button>
                    </Form.Item>
                  </Col>
                  {/* Display added columns */}

                  {columns.length > 0 && (
                    <Col span={24} className="mt-2">
                      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Added Columns:</div>
                      <Row gutter={[8, 8]}>
                        {columns.map((column, colIndex) => (
                          <Col span={24 / 4} key={colIndex}>
                            <Card size="small" bodyStyle={{ padding: "8px" }} style={{ fontSize: 12 }}>
                              <div style={{ marginBottom: 4 }}>
                                <strong>{column.column_name}</strong>
                              </div>
                              {column.set_value && (
                                <div style={{ color: "#666" }}>
                                  Set: <i>{column.set_value}</i>
                                </div>
                              )}
                              {column.merge_from_column && column.merge_to_column && (
                                <div style={{ color: "#666" }}>
                                  Merge: {column.merge_from_column} - {column.merge_to_column}
                                </div>
                              )}
                              {column.is_split && column.split_columns && (
                                <div style={{ color: "#1890ff" }}>
                                  Split: (
                                  {column.split_columns
                                    .map((sc) => sc.column_name)
                                    .filter(Boolean)
                                    .join(", ")}
                                  )
                                </div>
                              )}
                              {!view && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    display: "flex",
                                    gap: 4,
                                    justifyContent: "flex-end",
                                    borderTop: "1px solid #f0f0f0",
                                    paddingTop: 8,
                                  }}
                                >
                                  <Button
                                    size="middle"
                                    icon={<EditOutlined />}
                                    type="text"
                                    onClick={() => showModal(colIndex)}
                                  ></Button>
                                  <Button
                                    size="middle"
                                    icon={<CloseOutlined />}
                                    type="text"
                                    danger
                                    onClick={() => removeColumn(colIndex)}
                                  ></Button>
                                </div>
                              )}
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              </Card>
            </Form>
          </div>
        </Panel>
      </Collapse>

      {/* Add Column Drawer */}
      <Drawer
        title={editingColumnIndex !== null ? "Edit Column" : "Add Column"}
        open={isDrawerOpen}
        onClose={handleModalCancel}
        width={900}
        maskClosable={false}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button onClick={handleModalCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleModalOk} type="primary">
              Confirm Column
            </Button>
          </div>
        }
      >
        <Form layout="vertical" form={modalForm}>
          <Form.Item
            label="Column Name"
            name="column_name"
            rules={[
              {
                required: true,
                message: "Column Name is required",
              },
            ]}
          >
            <Input placeholder="Markup Value" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Set Value" name="set_value">
                <Select placeholder="Value" showSearch allowClear>
                  {variables?.map((variable) => (
                    <Option key={variable.slug} value={variable.slug}>
                      {variable.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Merge From Column" name="merge_from_column">
                <Input placeholder="Eg, A6" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Merge To Column" name="merge_to_column">
                <Input placeholder="Eg, B6" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Calculation Rule" name="calculation_rule">
            <Input placeholder="Rule" />
          </Form.Item>

          <Form.Item label="Split any column into multiple columns?" name="is_split">
            <Radio.Group
              onChange={(e) => {
                setIsSplitColumn(e.target.value === 1);
              }}
              value={isSplitColumn ? 1 : 0}
              defaultValue={0}
            >
              <Radio value={1}>Yes</Radio>
              <Radio value={0}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {isSplitColumn && (
            <div style={{ marginTop: 16, padding: 16, background: "#f5f5f5", borderRadius: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h4 style={{ margin: 0 }}>Split Columns</h4>
                {/* <Button type="primary" ghost size="small" onClick={addSplitColumn} icon={<PlusOutlined />}>
                  Add Column
                </Button> */}
              </div>

              {splitColumns.map((splitCol, idx) => (
                <Card
                  key={splitCol.id}
                  size="small"
                  className="mb-2"
                  title={`Column ${idx + 1}`}
                  extra={
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Button type="primary" ghost size="middle" onClick={addSplitColumn} icon={<PlusOutlined />}>
                        Add Column
                      </Button>
                      {splitColumns.length > 1 && (
                        <Button
                          type="text"
                          danger
                          size="middle"
                          icon={<CloseOutlined />}
                          onClick={() => removeSplitColumn(splitCol.id)}
                        />
                      )}
                    </div>
                  }
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span>
                            Column Name <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        style={{ marginBottom: 8 }}
                        validateStatus={
                          showSplitColumnValidation && (!splitCol.column_name || splitCol.column_name.trim() === "")
                            ? "error"
                            : ""
                        }
                        help={
                          showSplitColumnValidation && (!splitCol.column_name || splitCol.column_name.trim() === "")
                            ? "Column name is required"
                            : ""
                        }
                      >
                        <Input
                          placeholder="Name"
                          value={splitCol.column_name}
                          onChange={(e) => updateSplitColumn(splitCol.id, "column_name", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Set Value" style={{ marginBottom: 8 }}>
                        <Select
                          placeholder="Value"
                          showSearch
                          allowClear
                          value={splitCol.set_value || undefined}
                          onChange={(value) => updateSplitColumn(splitCol.id, "set_value", value)}
                        >
                          {variables?.map((variable) => (
                            <Option key={variable.slug} value={variable.slug}>
                              {variable.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Calculation Rule" style={{ marginBottom: 8 }}>
                        <Input
                          placeholder="Rule"
                          value={splitCol.calculation_rule}
                          onChange={(e) => updateSplitColumn(splitCol.id, "calculation_rule", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )}
        </Form>
      </Drawer>
    </>
  );
});

export default AddTableForm;
